import { useState, useEffect } from "react";
import { getJobs, createJob, updateJob, deleteJob } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AdminSideNav } from "@/pages/admin/AdminDashboardPage";
import BrandedHeader from "@/components/BrandedHeader";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Brain, Plus, Pencil, Trash2, Loader2, Briefcase, X, Power, PowerOff } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const ROLE_TYPES = ["Sales", "Technical", "Operations"];
const SENIORITY_LEVELS = ["Junior", "Mid", "Senior", "Lead"];

const defaultJobForm = {
  title: "",
  description: "",
  required_skills: [],
  role_type: "Sales",
  seniority_level: "Mid",
  department: "",
  location: "",
  target_duration_minutes: 30,
  proctor_enabled: true,  // Added - required by backend
  is_active: true,
};

export default function AdminJobsPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [form, setForm] = useState({ ...defaultJobForm });
  const [skillInput, setSkillInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadJobs(); }, []);

  const loadJobs = async () => {
    try {
      const res = await getJobs();
      setJobs(res.data);
    } catch (e) {
      toast.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingJob(null);
    setForm({ ...defaultJobForm });
    setDialogOpen(true);
  };

  const openEdit = (job) => {
    setEditingJob(job);
    setForm({
      title: job.title,
      description: job.description,
      required_skills: job.required_skills || [],
      role_type: job.role_type,
      seniority_level: job.seniority_level,
      department: job.department || "",
      location: job.location || "",
      target_duration_minutes: job.target_duration_minutes || 30,
      proctor_enabled: job.proctor_enabled !== false,  // Added
      is_active: job.is_active !== false,
    });
    setDialogOpen(true);
  };

  const addSkill = () => {
    if (skillInput.trim() && !form.required_skills.includes(skillInput.trim())) {
      setForm({ ...form, required_skills: [...form.required_skills, skillInput.trim()] });
      setSkillInput("");
    }
  };

  const removeSkill = (skill) => {
    setForm({ ...form, required_skills: form.required_skills.filter(s => s !== skill) });
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.description.trim()) { toast.error("Description is required"); return; }
    if (form.required_skills.length === 0) { toast.error("Add at least one skill"); return; }
    
    setSaving(true);
    try {
      if (editingJob) {
        await updateJob(editingJob.id, form);
        toast.success("Job updated");
      } else {
        await createJob(form);
        toast.success("Job created");
      }
      setDialogOpen(false);
      loadJobs();
    } catch (e) {
      console.error("Job save error:", e);
      const errorMsg = e.response?.data?.detail || e.message || "Failed to save job";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm("Delete this job?")) return;
    try {
      await deleteJob(jobId);
      toast.success("Job deleted");
      loadJobs();
    } catch (e) {
      toast.error("Failed to delete job");
    }
  };

  const handleToggleActive = async (job) => {
    try {
      await updateJob(job.id, { ...job, is_active: !job.is_active });
      toast.success(`Job ${!job.is_active ? 'activated' : 'deactivated'}`);
      loadJobs();
    } catch (e) {
      toast.error("Failed to update job status");
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <AdminSideNav active="Jobs" />
      
      <div className="flex-1 flex flex-col">
        <BrandedHeader className="lg:hidden">
          <h1 className="text-lg font-display font-semibold text-center">Job Management</h1>
        </BrandedHeader>

        <div className="px-4 sm:px-6 lg:px-8 py-6 flex-1">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-display font-semibold">Jobs</h1>
                <p className="text-muted-foreground text-sm mt-1">Manage interview positions</p>
              </div>
              <Button onClick={openCreate} className="btn-press" data-testid="admin-jobs-create-button">
                <Plus className="w-4 h-4 mr-2" /> Create Job
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 text-primary animate-spin" />
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-20">
                <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No jobs created yet</p>
                <Button onClick={openCreate} variant="secondary" className="mt-4">
                  <Plus className="w-4 h-4 mr-2" /> Create Your First Job
                </Button>
              </div>
            ) : (
              <div className="border border-border rounded-xl overflow-hidden" data-testid="admin-jobs-table">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-secondary/30">
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Title</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 hidden sm:table-cell">Type</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 hidden md:table-cell">Seniority</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Skills</th>
                      <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Status</th>
                      <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map(job => (
                      <tr key={job.id} className="border-b border-border last:border-0 hover:bg-secondary/20 transition-colors">
                        <td className="px-4 py-3">
                          <p className="text-sm font-medium">{job.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{job.description}</p>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <Badge variant="secondary" className="text-xs">{job.role_type}</Badge>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-sm text-muted-foreground">{job.seniority_level}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell">
                          <div className="flex flex-wrap gap-1">
                            {job.required_skills?.slice(0, 3).map((s, i) => (
                              <Badge key={i} variant="outline" className="text-xs">{s}</Badge>
                            ))}
                            {(job.required_skills?.length || 0) > 3 && (
                              <Badge variant="outline" className="text-xs">+{job.required_skills.length - 3}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className={`text-xs ${job.is_active ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}>
                            {job.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {isSuperAdmin && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`h-8 w-8 ${job.is_active ? 'text-[hsl(var(--success))]' : 'text-muted-foreground'}`}
                                onClick={() => handleToggleActive(job)}
                                title={job.is_active ? 'Deactivate job' : 'Activate job'}
                                data-testid="toggle-job-status"
                              >
                                {job.is_active ? <Power className="w-3.5 h-3.5" /> : <PowerOff className="w-3.5 h-3.5" />}
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(job)} data-testid="admin-job-edit-button">
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-[hsl(var(--destructive))]" onClick={() => handleDelete(job.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display">{editingJob ? 'Edit Job' : 'Create New Job'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Title *</label>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Senior Sales Executive" className="bg-card" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Description *</label>
              <textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Describe the role..."
                rows={3}
                className="flex w-full rounded-lg border border-input bg-card px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Role Type</label>
                <Select value={form.role_type} onValueChange={v => setForm({ ...form, role_type: v })}>
                  <SelectTrigger className="bg-card"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Seniority</label>
                <Select value={form.seniority_level} onValueChange={v => setForm({ ...form, seniority_level: v })}>
                  <SelectTrigger className="bg-card"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SENIORITY_LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Required Skills *</label>
              <div className="flex gap-2">
                <Input
                  value={skillInput}
                  onChange={e => setSkillInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                  placeholder="Add a skill..."
                  className="bg-card"
                />
                <Button type="button" variant="secondary" onClick={addSkill} size="sm">Add</Button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.required_skills.map((skill, i) => (
                  <Badge key={i} variant="secondary" className="text-xs flex items-center gap-1">
                    {skill}
                    <button onClick={() => removeSkill(skill)} className="hover:text-[hsl(var(--destructive))]">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Department</label>
                <Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} placeholder="Sales" className="bg-card" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Location</label>
                <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Remote" className="bg-card" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Interview Duration (min)</label>
              <Input type="number" value={form.target_duration_minutes} onChange={e => setForm({ ...form, target_duration_minutes: parseInt(e.target.value) || 30 })} className="bg-card" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="btn-press" data-testid="admin-job-form-submit-button">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingJob ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
